/**
 * FamilyStatusView — deliberately minimal family-facing screen.
 *
 * One screen. No matching, no payments, no clutter:
 *   - Who is coming (clinician display, no PHI)
 *   - When (first-visit window, shared with planner/provider)
 *   - One human status line
 *
 * Gated by `family_view` entitlement. Default-deny: if the caller
 * lacks an authorization for any of their authorized patients, the
 * "no access" friendly screen renders.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, CalendarClock, Stethoscope, Loader2, Lock, ShieldCheck,
  Clock, AlertTriangle, ExternalLink, ClipboardCheck, Circle, CheckCircle2, Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrgEntitlements } from '../hooks/useOrgEntitlements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatVisitTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: 'long', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function FamilyStatusView() {
  const { loading: entLoading, hasFlag } = useOrgEntitlements();
  const { user } = useAuth();

  if (entLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }
  if (!hasFlag('family_view')) {
    return <FamilyViewLocked isPatient={user?.role === 'patient'} />;
  }
  return <FamilyStatusInner />;
}

const FamilyViewLocked = ({ isPatient }) => (
  <div className="min-h-[60vh] flex items-center justify-center px-6"
        data-testid="family-view-locked">
    <div className="max-w-xl text-center space-y-5">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100">
        <Lock className="w-7 h-7 text-slate-500" />
      </div>
      <h1 className="text-3xl font-semibold text-slate-900">
        Family view isn&apos;t enabled here yet
      </h1>
      <p className="text-base text-slate-600 leading-relaxed">
        Family view is a calm, read-only window into a loved one&apos;s
        post-acute care — who&apos;s coming and when. It opens only after
        the patient explicitly authorizes you.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
        {/* Iter 163 · Give the patient viewing this page a way to start
            the invite flow directly instead of only offering the Ops email. */}
        {isPatient && (
          <Link to="/patient/family"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
              data-testid="family-view-open-portal">
            Open Family Portal
          </Link>
        )}
        <a href="mailto:ops@caraloom.com?subject=Enable%20Family%20View"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium ${
              isPatient
                ? 'border border-slate-200 text-slate-700 hover:bg-slate-50'
                : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
            data-testid="family-view-contact-ops">
          <ExternalLink className="w-4 h-4" /> Email Ops
        </a>
      </div>
    </div>
  </div>
);

const CHECKLIST_STATUS = {
  complete:      { label: 'On file',        tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  signed:        { label: 'Signed',         tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  documented:    { label: 'Documented',     tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  in_discussion: { label: 'In discussion',  tone: 'bg-slate-100 text-slate-600 border-slate-200' },
  not_started:   { label: 'Not started',    tone: 'bg-slate-50 text-slate-500 border-slate-200' },
};

function AcpChecklist({ patientId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    let live = true;
    axios.get(`${API}/family/acp-checklist?patient_id=${encodeURIComponent(patientId)}`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (live) { setData(res.data); setDenied(false); } })
      .catch((e) => {
        if (live) setDenied(true); // 403 (no acp scope) or 402 — hide quietly
      });
    return () => { live = false; };
  }, [patientId, token]);

  if (denied || !data) return null;
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm"
          data-testid="family-acp-checklist">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck className="w-5 h-5 text-rose-600" />
        <h2 className="text-lg font-semibold text-slate-900">Decisions to make</h2>
      </div>
      <p className="text-xs text-slate-500 mb-5">
        Advance-care-planning steps, as documented with the care team.
      </p>
      <ul className="space-y-3">
        {data.checklist.map((item) => {
          const s = CHECKLIST_STATUS[item.status] || CHECKLIST_STATUS.not_started;
          return (
            <li key={item.key} className="flex items-start gap-3" data-testid={`acp-check-${item.key}`}>
              {item.complete
                ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                : <Circle className="w-5 h-5 text-slate-300 mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-slate-900">{item.label}</span>
                  <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border ${s.tone}`}>{s.label}</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{item.hint}</p>
                {item.key === 'goc_conversation' && item.last_discussed_at && (
                  <p className="text-xs text-slate-400 mt-0.5">Last discussed {item.last_discussed_at.slice(0, 10)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {data.follow_up?.next_review_at && (
        <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-600 inline-flex items-center gap-1.5"
              data-testid="acp-checklist-followup">
          <Clock className="w-3.5 h-3.5 text-amber-600" />
          Next re-discussion: {data.follow_up.next_review_at.slice(0, 10)}
        </div>
      )}
      <div className="mt-4 flex items-start gap-1.5 text-[0.7rem] text-slate-400">
        <Info className="w-3 h-3 shrink-0 mt-0.5" />
        {data.decision_support_notice}
      </div>
    </div>
  );
}

function FamilyStatusInner() {
  const { token, user } = useAuth();
  const [auths, setAuths] = useState([]);
  const [selectedPid, setSelectedPid] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);

  // Load active authorizations for caller
  const loadAuths = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/family/authorizations`,
        { headers: { Authorization: `Bearer ${token}` } });
      const rows = res.data.rows || [];
      setAuths(rows);
      // Auto-select first patient (caregivers usually authorize 1)
      if (rows.length > 0) {
        // Caregiver path: select the patient_id from the auth row.
        // Patient path: select self.
        if (user?.role === 'patient') {
          setSelectedPid(user.id);
        } else {
          setSelectedPid(rows[0].patient_id);
        }
      } else if (user?.role === 'patient') {
        setSelectedPid(user.id);
      }
    } catch (e) {
      toast.error('Failed to load access list');
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => { loadAuths(); }, [loadAuths]);

  // Load status for the selected patient
  const loadStatus = useCallback(async () => {
    if (!selectedPid) { setStatus(null); return; }
    setStatusLoading(true);
    try {
      const res = await axios.get(
        `${API}/family/status?patient_id=${encodeURIComponent(selectedPid)}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        setStatus({ _denied: true, _message: detail?.message || 'Not authorized' });
      } else {
        toast.error('Failed to load status');
      }
    } finally {
      setStatusLoading(false);
    }
  }, [token, selectedPid]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  // No authorizations + non-patient → friendly no-access state
  if (auths.length === 0 && user?.role !== 'patient') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6"
            data-testid="family-no-authorizations">
        <div className="max-w-xl text-center space-y-5">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-100">
            <ShieldCheck className="w-7 h-7 text-slate-500" />
          </div>
          <h1 className="text-3xl font-semibold text-slate-900">
            Waiting for an invite
          </h1>
          <p className="text-base text-slate-600 leading-relaxed">
            A patient hasn&apos;t shared access with you yet. Once they
            authorize you, this screen will quietly fill in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8" data-testid="family-status-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center">
            <Heart className="w-6 h-6 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {status?.patient_display || (user?.role === 'patient' ? 'You' : 'Family')}
            </h1>
            <p className="text-xs text-slate-500">
              Post-acute care overview
            </p>
          </div>
        </div>
      </div>

      {/* Patient picker for caregivers with multiple authorizations */}
      {user?.role !== 'patient' && auths.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="family-patient-picker">
          {auths.map((a) => (
            <button key={a.id}
                    onClick={() => setSelectedPid(a.patient_id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      selectedPid === a.patient_id
                        ? 'bg-slate-900 text-white border-slate-900'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`patient-pick-${a.patient_id}`}>
              {a.relationship || 'Patient'}
            </button>
          ))}
        </div>
      )}

      {/* Calm status card */}
      <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm"
            data-testid="family-status-card">
        {statusLoading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading status…
          </div>
        ) : !status ? (
          <p className="text-sm text-slate-500 text-center py-6">
            No status to show.
          </p>
        ) : status._denied ? (
          <div className="text-center py-6 space-y-3">
            <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto" />
            <p className="text-sm text-slate-700">{status._message}</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="family-status-content">
            {/* The single calm status line */}
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Status</div>
              <p className="text-xl text-slate-900 leading-relaxed font-medium"
                  data-testid="family-status-line">
                {status.status_line}
              </p>
            </div>

            {/* Clinician */}
            {status.clinician_display && (
              <div className="flex items-start gap-3 py-4 border-t border-slate-100"
                    data-testid="family-clinician">
                <div className="w-9 h-9 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mt-0.5">
                  <Stethoscope className="w-4 h-4 text-emerald-700" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Assigned clinician
                  </div>
                  <div className="text-base font-medium text-slate-900">
                    {status.clinician_display}
                  </div>
                  {status.provider_org_display && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      {status.provider_org_display}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* First visit */}
            {status.first_visit_at && (
              <div className="flex items-start gap-3 py-4 border-t border-slate-100"
                    data-testid="family-first-visit">
                <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mt-0.5">
                  <CalendarClock className="w-4 h-4 text-blue-700" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    First visit
                  </div>
                  <div className="text-base font-medium text-slate-900">
                    {formatVisitTime(status.first_visit_at)}
                  </div>
                  {status.first_visit_committed_at && (
                    <div className="text-xs text-slate-500 mt-0.5">
                      Confirmed {formatVisitTime(status.first_visit_committed_at)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Next touchpoint */}
            {status.next_touchpoint_at && (
              <div className="flex items-start gap-3 py-4 border-t border-slate-100"
                    data-testid="family-next-touchpoint">
                <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center mt-0.5">
                  <Clock className="w-4 h-4 text-amber-700" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Next check-in
                  </div>
                  <div className="text-base font-medium text-slate-900">
                    {formatVisitTime(status.next_touchpoint_at)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ACP "Decisions to make" checklist — renders only when the patient
          shared the acp scope (or for the patient themselves). Read-only,
          descriptive, never advisory. */}
      <AcpChecklist patientId={selectedPid} />

      {/* Quiet footer — privacy reassurance */}
      <div className="text-center text-xs text-slate-400 pt-4 border-t border-slate-100">
        Read-only · You see what the patient explicitly authorizes
      </div>

      {/* Patient-only: manage authorizations */}
      {user?.role === 'patient' && (
        <div className="text-center">
          <Link to="/family/share"
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
                data-testid="family-share-link">
            Manage who can see this →
          </Link>
        </div>
      )}
    </div>
  );
}
